import { OktaAuth } from '@okta/okta-auth-js';
import Config from '../../config/Config';
import { OktaToken } from './interfaces/OktaAuth';

/** localStorage key used by Okta tokenManager (okta-token-storage). */
export const OKTA_TOKEN_STORAGE_KEY = 'okta-token-storage';

/** Emails that are always treated as administrators (app override in addition to Okta claim/group). */
const ADMIN_EMAILS = [
  'timothy.leuthold@scottfamily.org',
  'heather.skelton@scottfamily.org',
];

/** Parsed shape of id/access token entries in okta-token-storage. */
interface StoredTokenEntry {
  idToken?: string;
  accessToken?: string;
}

interface OktaTokenStorage {
  idToken?: StoredTokenEntry;
  accessToken?: StoredTokenEntry;
}

/**
 * Read the id_token string directly from localStorage key okta-token-storage.
 * Use when you need the token synchronously (e.g. API headers). Returns null if missing or invalid.
 */
export const getStoredIdTokenString = (): string | null => {
  try {
    const raw = typeof window !== 'undefined' ? window.localStorage.getItem(OKTA_TOKEN_STORAGE_KEY) : null;
    if (!raw) return null;
    const data = JSON.parse(raw) as OktaTokenStorage;
    const token = data?.idToken?.idToken;
    return typeof token === 'string' ? token : null;
  } catch {
    return null;
  }
};

/**
 * Read the access_token string directly from localStorage key okta-token-storage.
 * Use when you need the token synchronously (e.g. Bearer header). Returns null if missing or invalid.
 */
export const getStoredAccessTokenString = (): string | null => {
  try {
    const raw = typeof window !== 'undefined' ? window.localStorage.getItem(OKTA_TOKEN_STORAGE_KEY) : null;
    if (!raw) return null;
    const data = JSON.parse(raw) as OktaTokenStorage;
    const token = data?.accessToken?.accessToken;
    return typeof token === 'string' ? token : null;
  } catch {
    return null;
  }
};

export const oktaConfig = {
  issuer: Config.oktaIssuer,
  clientId: Config.oktaClientId,
  redirectUri: Config.oktaRedirectUri,
  baseUri: Config.oktaBaseUri,
  url: Config.oktaUrl,
};

// Single shared instance: used by Security/LoginCallback and by CheckAuthentication/GetAccessToken/Logout
export const oktaAuth = new OktaAuth({
  issuer: oktaConfig.issuer,
  clientId: oktaConfig.clientId,
  redirectUri: window.location.origin + '/login/callback',
  scopes: ['openid', 'profile', 'email', 'offline_access'],
});

/**
 * After login, Okta redirects to /login/callback. LoginCallback runs oktaAuth.handleLoginRedirect(),
 * which calls the token endpoint (oauth2/v1/token), gets id_token and access_token, and stores
 * them in oktaAuth.tokenManager. These helpers read those tokens from tokenManager.
 */

/**
 * Get the raw id_token string from the token endpoint response (stored in tokenManager by LoginCallback).
 */
export const getIdTokenString = async (): Promise<string | null> => {
  try {
    const idToken = await oktaAuth.tokenManager.get('idToken');
    if (!idToken) return null;
    const raw = (idToken as any).idToken ?? (idToken as any).accessToken ?? idToken;
    return typeof raw === 'string' ? raw : null;
  } catch {
    return null;
  }
};

/**
 * Get the raw access_token string from the token endpoint response (stored in tokenManager by LoginCallback).
 */
export const getAccessTokenString = async (): Promise<string | null> => {
  try {
    const accessToken = await oktaAuth.tokenManager.get('accessToken');
    if (!accessToken) return null;
    const raw = (accessToken as any).accessToken ?? (accessToken as any).value ?? accessToken;
    return typeof raw === 'string' ? raw : null;
  } catch {
    return null;
  }
};

/**
 * Map Okta Auth JS v7 token to app's OktaToken shape (claims used across the app).
 */
function toAppToken(idToken: any): OktaToken | null {
  if (!idToken || !idToken.claims) return null;
  const claims = idToken.claims as Record<string, unknown>;
  const email = (claims.email as string) ?? (claims.preferred_username as string) ?? '';
  const fromOkta = (claims.is_administrator as boolean) ?? (claims.groups as string[] || []).some((g: string) => g.toLowerCase().includes('admin'));
  const fromAppList = ADMIN_EMAILS.some((e) => e.toLowerCase() === email.toLowerCase());
  return {
    expiresAt: idToken.expiresAt ?? 0,
    claims: {
      email,
      name: (claims.name as string) ?? '',
      preferred_username: (claims.preferred_username as string) ?? '',
      is_administrator: fromOkta || fromAppList,
    },
    idToken: typeof idToken === 'string' ? idToken : (idToken as any).idToken ?? '',
  } as OktaToken;
}

export const CheckAuthentication = async (): Promise<OktaToken | null> => {
  try {
    const idToken = await oktaAuth.tokenManager.get('idToken');
    if (!idToken) return null;
    return toAppToken(idToken);
  } catch {
    oktaAuth.tokenManager.clear();
    return null;
  }
};

export const GetAccessToken = async (): Promise<OktaToken | null> => {
  try {
    const accessToken = await oktaAuth.tokenManager.get('accessToken');
    if (!accessToken) return null;
    // Callers (e.g. ScottlandApi) expect { accessToken: string } for Bearer header
    const tokenString =
      (accessToken as any).accessToken ??
      (typeof accessToken === 'string' ? accessToken : (accessToken as any).value);
    return tokenString ? { ...(accessToken as any), accessToken: tokenString } as unknown as OktaToken : null;
  } catch {
    oktaAuth.tokenManager.clear();
    return null;
  }
};

/**
 * Log out from Okta and clear local tokens. Call this first; do not clear Redux or
 * navigate before it completes, or the browser may navigate away and cancel the Okta logout request.
 * When Okta supports it, postLogoutRedirectUri sends the user back to the app root after logout.
 */
/** Known Okta storage keys (SDK may use custom storageKey; we clear all okta-* keys). */
const OKTA_STORAGE_KEYS = [
  OKTA_TOKEN_STORAGE_KEY,
  'okta-cache-storage',
  'okta-pkce-storage',
  'okta-transaction-storage',
  'okta-shared-transaction-storage',
  'okta-original-uri-storage',
  'okta-idx-response-storage',
  'oktaStateToken',
];

/**
 * Clear Okta tokenManager and all Okta-related data from localStorage and sessionStorage.
 * Call this before signOut so tokens are removed even if signOut redirects immediately.
 */
function clearOktaStorage(): void {
  oktaAuth.tokenManager.clear();
  if (typeof window === 'undefined') return;
  const clearStorage = (storage: Storage) => {
    try {
      OKTA_STORAGE_KEYS.forEach((key) => storage.removeItem(key));
      const keysToRemove: string[] = [];
      for (let i = 0; i < storage.length; i++) {
        const key = storage.key(i);
        if (key && key.toLowerCase().startsWith('okta')) keysToRemove.push(key);
      }
      keysToRemove.forEach((key) => storage.removeItem(key));
    } catch {
      // ignore
    }
  };
  clearStorage(window.localStorage);
  clearStorage(window.sessionStorage);
}

/**
 * Log out from Okta and clear local tokens/cache. Clears tokenManager and storage first,
 * then calls signOut for server session. Do not clear Redux or navigate before this completes,
 * or the app may cancel the Okta logout request.
 */
export const Logout = async (): Promise<void> => {
  clearOktaStorage();
  try {
    const postLogoutRedirectUri = typeof window !== 'undefined' ? window.location.origin + '/' : undefined;
    await oktaAuth.signOut({ postLogoutRedirectUri });
  } finally {
    clearOktaStorage();
  }
};

/** Options for getWithoutPrompt (silent auth). */
export interface GetWithoutPromptOptions {
  /** Username/email hint for Okta (no redirect). */
  loginHint?: string;
  /** Session token from Auth API (e.g. after username/password). Optional if user has existing Okta session. */
  sessionToken?: string;
  /** Scopes to request. Defaults to openid, email, profile, offline_access. */
  scopes?: string[];
  /** Response type: 'id_token' or 'code' (PKCE). SDK default is typically 'code' when pkce is true. */
  responseType?: 'id_token' | 'code' | string | string[];
  /** IdP for social/custom identity provider. */
  idp?: string;
}

const DEFAULT_GET_WITHOUT_PROMPT_SCOPES = ['openid', 'email', 'profile', 'offline_access'];

/**
 * Get tokens without user prompt (silent auth via iframe using existing Okta session or sessionToken).
 * Returns true if tokens were obtained and stored; false on failure (caller can fall back to signInWithRedirect).
 *
 * Example:
 *   authClient.token.getWithoutPrompt({ responseType: 'id_token', sessionToken, scopes, ... })
 *     .then(res => authClient.tokenManager.setTokens(res.tokens))
 *     .catch(err => { ... });
 */
export const loginWithGetWithoutPrompt = async (opts: GetWithoutPromptOptions | string): Promise<boolean> => {
  try {
    const options = typeof opts === 'string'
      ? { loginHint: opts.trim(), scopes: DEFAULT_GET_WITHOUT_PROMPT_SCOPES }
      : {
          scopes: opts.scopes ?? DEFAULT_GET_WITHOUT_PROMPT_SCOPES,
          ...(opts.loginHint ? { loginHint: opts.loginHint.trim() } : {}),
          ...(opts.sessionToken ? { sessionToken: opts.sessionToken } : {}),
          ...(opts.responseType ? { responseType: opts.responseType } : {}),
          ...(opts.idp ? { idp: opts.idp } : {}),
        };
    const res = await oktaAuth.token.getWithoutPrompt(options);
    if (res?.tokens) {
      oktaAuth.tokenManager.setTokens(res.tokens as any);
      return true;
    }
    return false;
  } catch {
    return false;
  }
};

/**
 * Sign in with username and password via Okta Auth API, then exchange session for OIDC tokens.
 * 1. authClient.signIn({ username, password }) -> transaction
 * 2. If transaction.status === 'SUCCESS' and sessionToken, getWithoutPrompt({ sessionToken, ... })
 * 3. tokenManager.setTokens(tokens); return app OktaToken (idToken claims).
 */
export const loginWithCredentials = async (username: string, password: string): Promise<OktaToken | null> => {
  try {
    let transaction: { status?: string; sessionToken?: string } | null = null;

    const credentials = { username: username.trim(), password };
    transaction = await (oktaAuth as any).signIn(credentials);

    if (transaction?.status === 'SUCCESS' && transaction.sessionToken) {
      const oAuthOptions = {
        sessionToken: transaction.sessionToken,
        responseType: ['id_token', 'token'] as const,
        scopes: ['openid', 'email', 'profile'],
        redirectUri: typeof window !== 'undefined' ? window.location.origin + '/login/callback' : oktaConfig.redirectUri,
      };
      const res = await oktaAuth.token.getWithoutPrompt(oAuthOptions);
      if (res?.tokens) {
        oktaAuth.tokenManager.setTokens(res.tokens as any);
        const idToken = await oktaAuth.tokenManager.get('idToken');
        return idToken ? toAppToken(idToken) : null;
      }
    }
    return null;
  } catch {
    return null;
  }
};

/** Start Okta hosted login (redirect). Callback is handled by LoginCallback at /login/callback. */
export const signInWithRedirect = (): Promise<void> => oktaAuth.signInWithRedirect();
